import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  EmptyState,
  Modal,
  SearchMultiSelect,
  SearchSelect,
  Select,
} from "@/components/common";
import { extractErrorMessage } from "@/services/axiosConfig";
import { useClients } from "@/hooks/useClients";
import { useContractTemplates } from "@/hooks/useContractTemplates";
import { useMembers } from "@/hooks/useInvitations";
import { useCreateContract } from "@/hooks/useContracts";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useTeses } from "@/hooks/useTeses";
import { useWorkspaceSettings } from "@/hooks/useWorkspaces";
import type {
  ContractCreatePayload,
  IndicacaoCreatePayload,
  IndicacaoTipo,
  OperatorType,
  SignatureMethod,
} from "@/types/contract";
import type { ContractTemplate } from "@/types/contractTemplate";
import { addMonths } from "@/utils/date";
import { formatBRL } from "@/utils/fmt";
import { getInitials } from "@/utils/initials";
import { fractionToPct, pctToFraction } from "@/utils/pct";
import { computeCascade } from "./cascade";

const STEPS = ["Template", "Empresa", "Escopo e variáveis", "Vigência e assinatura", "Revisão"] as const;

const DEFAULT_TAXA = "0.0615";

const OPERATOR_LABEL: Record<OperatorType, string> = {
  tributo_devido: "Tributo Devido",
  organization: "Organização",
  workspace: "Workspace",
};

type DraftIndicacao = {
  indicado_user_id: string;
  tipo: IndicacaoTipo;
  percent_pct: string; // user-entered as %
  taxa_pct: string; // user-entered as %
};

type Props = {
  workspaceId: number;
  onClose: () => void;
};

export function ContractWizard({ workspaceId, onClose }: Props) {
  const navigate = useNavigate();
  const create = useCreateContract(workspaceId);

  const { data: templates = [] } = useContractTemplates(workspaceId, { onlyActive: true });
  const { data: clients = [] } = useClients(workspaceId);
  const { data: teses = [] } = useTeses(true);
  const { data: orgs = [] } = useOrganizations(workspaceId);
  const { data: members = [] } = useMembers(workspaceId);
  const { data: wsSettings } = useWorkspaceSettings(workspaceId);

  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // step 0 — template
  const [templateId, setTemplateId] = useState<number | null>(null);
  const template = templates.find((t) => t.id === templateId) ?? null;
  // step 1 — empresa
  const [clientId, setClientId] = useState<number | null>(null);
  // step 2 — escopo e variáveis
  const [teseIds, setTeseIds] = useState<number[]>([]);
  const [successFeePct, setSuccessFeePct] = useState("20"); // %
  // Participação TD: começa vazio. Para operador != tributo_devido é obrigatório
  // (preenchido pelo default da org/workspace, pelo template ou à mão) — nunca
  // assumimos 100% silenciosamente. Tributo Devido fixa 100% via effectiveTdShare.
  const [tdSharePct, setTdSharePct] = useState(""); // %
  // Once the user edits the participação TD field, stop auto pre-filling it.
  const tdShareTouched = useRef(false);
  // step 3 — vigência e assinatura
  const [indicacoes, setIndicacoes] = useState<DraftIndicacao[]>([]);
  const [dataInicio, setDataInicio] = useState("");
  const [validade, setValidade] = useState("");
  // Once the user edits validade, stop auto-suggesting it from the template.
  const validadeTouched = useRef(false);
  const [signatureMethod, setSignatureMethod] = useState<SignatureMethod | "">("");
  const [modelDocPath, setModelDocPath] = useState("");
  // step 4 — revisão
  const [recuperado, setRecuperado] = useState("100000");

  // O operador NÃO é escolhido por contrato: vem da organização do cliente
  // (override) ou das configurações do workspace; default tributo_devido.
  const selectedClient = clients.find((c) => c.id === clientId) ?? null;
  const clientOrg = selectedClient
    ? (orgs.find((o) => o.id === selectedClient.organization_id) ?? null)
    : null;
  const resolvedOperator: OperatorType =
    clientOrg?.operator_type ?? wsSettings?.operator_type ?? "tributo_devido";

  const isTributoDevido = resolvedOperator === "tributo_devido";
  const effectiveTdShare = isTributoDevido ? "1.0" : pctToFraction(tdSharePct);

  const cascade = useMemo(
    () =>
      computeCascade({
        recuperado: Number(recuperado) || 0,
        success_fee_pct: pctToFraction(successFeePct),
        td_share_pct: effectiveTdShare,
        indicacoes: indicacoes.map((i, idx) => ({
          label: memberName(members, i.indicado_user_id) || `Indicação ${idx + 1}`,
          percent: pctToFraction(i.percent_pct),
          taxa_imposto_pct: pctToFraction(i.taxa_pct),
        })),
      }),
    [recuperado, successFeePct, effectiveTdShare, indicacoes, members],
  );

  /** Pre-fill wizard state from the chosen template. */
  function applyTemplate(t: ContractTemplate | null) {
    if (!t) return;
    const feePct = fractionToPct(t.success_fee_pct);
    if (feePct != null) setSuccessFeePct(feePct);
    const sharePct = fractionToPct(t.td_share_pct);
    if (sharePct != null) {
      setTdSharePct(sharePct);
      // The template wins over the org/workspace default for participação TD.
      tdShareTouched.current = true;
    }
    setTeseIds(t.default_tese_ids);
    // vigencia_meses is consumed in step 3 to auto-suggest validade.
  }

  function handleTemplateChange(id: number | null) {
    setTemplateId(id);
    applyTemplate(templates.find((t) => t.id === id) ?? null);
  }

  function addIndicacao() {
    setIndicacoes((prev) => [
      ...prev,
      { indicado_user_id: "", tipo: "interno", percent_pct: "", taxa_pct: "6.15" },
    ]);
  }

  function updateIndicacao(idx: number, patch: Partial<DraftIndicacao>) {
    setIndicacoes((prev) => prev.map((i, n) => (n === idx ? { ...i, ...patch } : i)));
  }

  function removeIndicacao(idx: number) {
    setIndicacoes((prev) => prev.filter((_, n) => n !== idx));
  }

  /** Pre-fill participação TD from the org/workspace default, unless edited. */
  function prefillTdShare(next: string | null) {
    if (tdShareTouched.current) return;
    if (next != null) setTdSharePct(next);
  }

  /**
   * Select the client and pre-fill participação TD from the resolved operator's
   * default — the client's organization default wins, else the workspace default.
   */
  function handleClientChange(id: number | null) {
    setClientId(id);
    const client = clients.find((c) => c.id === id) ?? null;
    const org = client ? orgs.find((o) => o.id === client.organization_id) : undefined;
    const operator: OperatorType =
      org?.operator_type ?? wsSettings?.operator_type ?? "tributo_devido";
    if (operator === "organization") {
      prefillTdShare(fractionToPct(org?.default_td_share_pct ?? null));
    } else if (operator === "workspace") {
      prefillTdShare(fractionToPct(wsSettings?.default_td_share_pct));
    }
    // tributo_devido → field is hidden and forced to 100% via effectiveTdShare.
  }

  /** Set data_inicio and auto-suggest validade from the template's vigência. */
  function handleDataInicioChange(value: string) {
    setDataInicio(value);
    if (!validadeTouched.current && value && template?.vigencia_meses != null) {
      const suggested = addMonths(value, template.vigencia_meses);
      if (suggested) setValidade(suggested);
    }
  }

  // ─── step gating ─────────────────────────────────────────────────────
  const canNext = (): boolean => {
    switch (step) {
      case 0:
        return templateId != null;
      case 1:
        return clientId != null;
      case 2:
        if (teseIds.length === 0) return false;
        if (!successFeePct.trim() || Number(successFeePct) <= 0) return false;
        // Operador != tributo_devido exige participação TD explícita (sem 100% silencioso).
        if (!isTributoDevido && (!tdSharePct.trim() || Number(tdSharePct) <= 0)) return false;
        return true;
      case 3:
        if (!dataInicio || !validade) return false;
        if (validade < dataInicio) return false;
        return indicacoes.every(
          (i) => i.indicado_user_id.trim() !== "" && Number(i.percent_pct) > 0,
        );
      default:
        return true;
    }
  };

  // O wizard não persiste rascunho: fechar com dados preenchidos pede confirmação
  // para evitar descartar os passos por engano (ESC, click-out ou botão fechar).
  const isDirty =
    templateId != null ||
    clientId != null ||
    teseIds.length > 0 ||
    indicacoes.length > 0 ||
    dataInicio !== "" ||
    validade !== "" ||
    signatureMethod !== "" ||
    modelDocPath.trim() !== "";

  function requestClose() {
    if (isDirty && !window.confirm("Descartar este contrato? Os dados preenchidos serão perdidos.")) {
      return;
    }
    onClose();
  }

  async function submit() {
    if (clientId == null || templateId == null) return;
    setError(null);
    const payload: ContractCreatePayload = {
      client_id: clientId,
      template_id: templateId,
      data_inicio: dataInicio,
      validade,
      // operator_type / operator_organization_id são derivados no backend.
      success_fee_pct: pctToFraction(successFeePct),
      td_share_pct: effectiveTdShare,
      signature_method: signatureMethod || null,
      model_document_path: modelDocPath.trim() || null,
      teses: teseIds,
      indicacoes: indicacoes.map<IndicacaoCreatePayload>((i) => ({
        indicado_user_id: Number(i.indicado_user_id),
        tipo: i.tipo,
        percent: pctToFraction(i.percent_pct),
        taxa_imposto_pct: i.taxa_pct.trim() ? pctToFraction(i.taxa_pct) : DEFAULT_TAXA,
      })),
    };
    try {
      const created = await create.mutateAsync(payload);
      onClose();
      navigate(`/contratos/${created.id}`);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  const footer = (
    <>
      {template && (
        <span className="td-faded" style={{ marginRight: "auto" }}>
          Template: <strong>{template.name}</strong>
        </span>
      )}
      {step > 0 && (
        <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>
          Voltar
        </Button>
      )}
      {step < STEPS.length - 1 ? (
        <Button variant="primary" disabled={!canNext()} onClick={() => setStep((s) => s + 1)}>
          Continuar
        </Button>
      ) : (
        <Button variant="primary" disabled={create.isPending} onClick={() => void submit()}>
          {create.isPending ? "Criando…" : "Criar contrato"}
        </Button>
      )}
    </>
  );

  return (
    <Modal
      onClose={requestClose}
      title="Novo contrato"
      subtitle={STEPS[step]}
      size="lg"
      footer={footer}
    >
      <div className="ctr-wizard-steps">
        {STEPS.map((_, i) => (
          <div
            key={i}
            className={`ctr-wizard-step ${i === step ? "active" : i < step ? "done" : ""}`}
          />
        ))}
      </div>

      {error && <p className="td-danger" style={{ marginBottom: 12 }}>{error}</p>}

      {step === 0 && (
        <div className="ctr-section">
          <p className="ctr-section-title">Template do contrato</p>
          {templates.length === 0 ? (
            <EmptyState
              icon="file-text"
              title="Nenhum template ativo"
              description="Todo contrato nasce de um template. Crie um template primeiro na aba Templates."
              action={
                <Button
                  variant="primary"
                  iconLeft="arrow-up-right"
                  onClick={() => {
                    onClose();
                    navigate("/contratos");
                  }}
                >
                  Ir para Templates
                </Button>
              }
            />
          ) : (
            <>
              <label className="ctr-field">
                <span>Template</span>
                <SearchSelect
                  value={templateId == null ? null : String(templateId)}
                  onChange={(v) => handleTemplateChange(v ? Number(v) : null)}
                  options={templates.map((t) => ({
                    id: String(t.id),
                    label: t.name,
                    sub: t.description ?? undefined,
                    mark: getInitials(t.name),
                  }))}
                  placeholder="Selecione o template…"
                  emptyLabel="Nenhum template encontrado."
                />
              </label>
              <p className="td-faded">
                O template pré-preenche operador, repasse, teses e vigência — tudo editável depois.
              </p>
            </>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="ctr-section">
          <p className="ctr-section-title">Empresa / cliente</p>
          {clients.length === 0 ? (
            <EmptyState
              icon="briefcase"
              title="Nenhuma empresa cadastrada"
              description="Cadastre uma empresa antes de criar o contrato."
              action={
                <Button
                  variant="primary"
                  iconLeft="plus"
                  onClick={() => {
                    onClose();
                    navigate("/empresas");
                  }}
                >
                  Cadastrar empresa
                </Button>
              }
            />
          ) : (
            <label className="ctr-field">
              <span>Cliente</span>
              <SearchSelect
                value={clientId == null ? null : String(clientId)}
                onChange={(v) => handleClientChange(v ? Number(v) : null)}
                options={clients.map((c) => ({
                  id: String(c.id),
                  label: c.legal_name,
                  sub: c.document_masked,
                  mark: getInitials(c.legal_name),
                }))}
                placeholder="Selecione a empresa…"
                emptyLabel="Nenhuma empresa encontrada."
              />
            </label>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="ctr-section">
          <p className="ctr-section-title">Teses tributárias cobertas</p>
          <label className="ctr-field">
            <span>Teses</span>
            <SearchMultiSelect
              values={teseIds.map(String)}
              onChange={(ids) => setTeseIds(ids.map(Number))}
              options={teses.map((t) => ({
                id: String(t.id),
                label: t.nome,
                sub: t.descricao ?? t.slug,
              }))}
              placeholder="Buscar e selecionar teses…"
              emptyLabel="Nenhuma tese encontrada."
            />
          </label>
          {teses.length === 0 && <EmptyState icon="file-text" title="Nenhuma tese ativa" />}

          <p className="ctr-section-title">Repasse e operador</p>
          <div className="ctr-term">
            <span>Operador</span>
            <strong>
              {OPERATOR_LABEL[resolvedOperator]}
              {resolvedOperator === "organization" && clientOrg
                ? ` · ${clientOrg.nickname ?? clientOrg.legal_name}`
                : ""}
            </strong>
          </div>
          <p className="td-faded">
            Definido na organização do cliente ou nas configurações do workspace — não editável por contrato.
          </p>

          <div className="ctr-field-row">
            <label className="ctr-field">
              <span>Success fee (%)</span>
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={successFeePct}
                onChange={(e) => setSuccessFeePct(e.target.value)}
              />
            </label>
            {!isTributoDevido && (
              <label className="ctr-field">
                <span>Participação TD (%)</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={tdSharePct}
                  placeholder="Obrigatório"
                  onChange={(e) => {
                    tdShareTouched.current = true;
                    setTdSharePct(e.target.value);
                  }}
                />
              </label>
            )}
          </div>
          {isTributoDevido ? (
            <p className="td-faded">
              Operador Tributo Devido — participação TD fixada em 100%.
            </p>
          ) : (
            <p className="td-faded">
              Informe a participação TD acordada com este operador. Sem default herdado da
              organização/workspace, o campo deve ser preenchido — não assumimos 100%.
            </p>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="ctr-section">
          <p className="ctr-section-title">Vigência</p>
          <div className="ctr-field-row">
            <label className="ctr-field">
              <span>Data de início</span>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => handleDataInicioChange(e.target.value)}
              />
            </label>
            <label className="ctr-field">
              <span>Validade</span>
              <input
                type="date"
                value={validade}
                onChange={(e) => {
                  validadeTouched.current = true;
                  setValidade(e.target.value);
                }}
              />
            </label>
          </div>
          {template?.vigencia_meses != null && (
            <p className="td-faded">
              Validade sugerida a partir do template ({template.vigencia_meses} meses) — editável.
            </p>
          )}
          {validade && dataInicio && validade < dataInicio && (
            <p className="td-danger">A validade deve ser maior ou igual à data de início.</p>
          )}

          <p className="ctr-section-title">Assinatura (opcional)</p>
          <div className="ctr-field-row">
            <label className="ctr-field">
              <span>Método de assinatura</span>
              <Select<SignatureMethod>
                value={signatureMethod || null}
                onChange={(v) => setSignatureMethod(v || "")}
                options={[
                  { value: "manual", label: "Manual" },
                  { value: "govbr", label: "Gov.br" },
                ]}
                placeholder="Definir depois"
                clearable
                clearLabel="Definir depois"
              />
            </label>
            <label className="ctr-field">
              <span>Caminho do modelo (doc)</span>
              <input
                type="text"
                value={modelDocPath}
                onChange={(e) => setModelDocPath(e.target.value)}
                placeholder="opcional"
              />
            </label>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <p className="ctr-section-title" style={{ margin: 0 }}>
              Indicações
            </p>
            <Button variant="ghost" size="sm" iconLeft="plus" onClick={addIndicacao}>
              Adicionar
            </Button>
          </div>
          {indicacoes.length === 0 && (
            <p className="td-faded">Nenhuma indicação. Opcional.</p>
          )}
          {indicacoes.map((ind, idx) => (
            <div key={idx} className="ctr-ind-row">
              <label className="ctr-field">
                <span>Indicado</span>
                <Select
                  value={ind.indicado_user_id || null}
                  onChange={(v) => updateIndicacao(idx, { indicado_user_id: v })}
                  options={members.map((m) => ({
                    value: String(m.user_id),
                    label: m.user_name ?? m.user_email_masked,
                  }))}
                  placeholder="Selecione…"
                />
              </label>
              <label className="ctr-field">
                <span>Tipo</span>
                <Select<IndicacaoTipo>
                  value={ind.tipo}
                  onChange={(v) => updateIndicacao(idx, { tipo: v })}
                  options={[
                    { value: "interno", label: "Interno" },
                    { value: "externo", label: "Externo" },
                  ]}
                />
              </label>
              <label className="ctr-field">
                <span>%</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={ind.percent_pct}
                  onChange={(e) => updateIndicacao(idx, { percent_pct: e.target.value })}
                />
              </label>
              <label className="ctr-field">
                <span>Imposto %</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={ind.taxa_pct}
                  onChange={(e) => updateIndicacao(idx, { taxa_pct: e.target.value })}
                />
              </label>
              <Button variant="ghost" size="sm" iconLeft="x" onClick={() => removeIndicacao(idx)} />
            </div>
          ))}
        </div>
      )}

      {step === 4 && (
        <div className="ctr-section">
          <p className="ctr-section-title">Revisão</p>
          <div className="ctr-terms">
            <div className="ctr-term">
              <span>Template</span>
              <strong>{template?.name ?? "—"}</strong>
            </div>
            <div className="ctr-term">
              <span>Cliente</span>
              <strong>
                {clients.find((c) => c.id === clientId)?.legal_name ?? `#${clientId ?? "?"}`}
              </strong>
            </div>
            <div className="ctr-term">
              <span>Teses</span>
              <strong>{teseIds.length}</strong>
            </div>
            <div className="ctr-term">
              <span>Operador</span>
              <strong>{OPERATOR_LABEL[resolvedOperator]}</strong>
            </div>
            <div className="ctr-term">
              <span>Success fee</span>
              <strong>{successFeePct}%</strong>
            </div>
            <div className="ctr-term">
              <span>Participação TD</span>
              <strong>{isTributoDevido ? "100%" : `${tdSharePct}%`}</strong>
            </div>
            <div className="ctr-term">
              <span>Vigência</span>
              <strong>
                {dataInicio || "—"} → {validade || "—"}
              </strong>
            </div>
          </div>

          <p className="ctr-section-title">Cascata (preview)</p>
          <label className="ctr-field">
            <span>Valor recuperado (exemplo)</span>
            <input
              type="number"
              min={0}
              value={recuperado}
              onChange={(e) => setRecuperado(e.target.value)}
            />
          </label>
          <CascadePreview cascade={cascade} />
        </div>
      )}
    </Modal>
  );
}

function memberName(
  members: { user_id: number; user_name: string | null; user_email_masked: string }[],
  userId: string,
): string {
  const id = Number(userId);
  const m = members.find((x) => x.user_id === id);
  return m ? (m.user_name ?? m.user_email_masked) : "";
}

export function CascadePreview({ cascade }: { cascade: ReturnType<typeof computeCascade> }) {
  return (
    <div className="ctr-cascade">
      <div className="ctr-cascade-row">
        <span>Valor recuperado</span>
        <span>{formatBRL(cascade.recuperado)}</span>
      </div>
      <div className="ctr-cascade-row">
        <span>Operador (success fee)</span>
        <span>{formatBRL(cascade.operador)}</span>
      </div>
      <div className="ctr-cascade-row">
        <span>TD recebe (participação)</span>
        <span>{formatBRL(cascade.td_recebe)}</span>
      </div>
      {cascade.indicacoes.map((ind, idx) => (
        <div key={idx} className="ctr-cascade-row indent">
          <span>
            {ind.label} (líquido)
          </span>
          <span>{formatBRL(ind.liquido)}</span>
        </div>
      ))}
      <div className="ctr-cascade-row total">
        <span>Caixa TD (após indicações)</span>
        <span>{formatBRL(cascade.caixa_td)}</span>
      </div>
      <div className="ctr-cascade-row total">
        <span>Cliente recebe</span>
        <span>{formatBRL(cascade.cliente)}</span>
      </div>
    </div>
  );
}
